import React, { useState } from 'react';
import { Slide } from './Slide';
import { Navigation } from './Navigation';
import { ChevronLeftIcon, ChevronRightIcon, RocketIcon, TargetIcon, BrainIcon, TrendingUpIcon, LayersIcon, WalletIcon, CalendarIcon, SendIcon, BriefcaseIcon, UserIcon } from 'lucide-react';
export function Presentation() {
  const [currentSlide, setCurrentSlide] = useState(0);
  // First, define all slides
  const allSlides = [
  // The Story slide
  {
    icon: <RocketIcon className="w-8 h-8 text-brand-purple" data-id="element-5" />,
    title: 'The Story',
    content: <div className="space-y-6" data-id="element-6">
          <div className="bg-white p-6 rounded-xl border border-gray-100" data-id="element-7">
            <h3 className="font-semibold text-brand-purple mb-3" data-id="element-8">
              Hey, I'm Irina 👋
            </h3>
            <p className="text-gray-700" data-id="element-9">
              After 17 years crushing it in tech sales and scaling 60+ projects,
              I've built something game-changing. Imagine having a sales expert
              (that's me) armed with cutting-edge AI to skyrocket your outreach
              game. No more generic pitches – we're talking next-level
              personalization that actually works.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4" data-id="element-10">
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100" data-id="element-11">
              <div className="text-3xl font-bold text-brand-purple mb-2" data-id="element-12">
                60+
              </div>
              <div className="text-sm text-gray-600" data-id="element-13">Tech Projects Scaled</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100" data-id="element-14">
              <div className="text-3xl font-bold text-brand-purple mb-2" data-id="element-15">
                17
              </div>
              <div className="text-sm text-gray-600" data-id="element-16">
                Years Making Sales Fun
              </div>
            </div>
          </div>
        </div>
  },
  // Momentum slide
  {
    icon: <TrendingUpIcon className="w-8 h-8 text-brand-purple" data-id="element-17" />,
    title: 'Momentum',
    content: <div className="space-y-4" data-id="element-18">
          <div className="bg-white p-5 rounded-xl border border-gray-100" data-id="element-19">
            <p className="text-sm text-gray-700" data-id="element-20">
              This isn't just another AI tool—it's the culmination of a 15-year
              journey in software sales leadership. Through Ready Set Sell,
              we've spent the last 5 years validating and perfecting process
              automation for B2B sales teams. Now, with advanced Language
              Models, we're revolutionizing personalization at scale.
            </p>
          </div>
          <div className="grid grid-cols-3 gap-3" data-id="element-21">
            <div className="bg-gradient-to-br from-brand-purple/5 to-brand-neon/5 p-3 rounded-xl border border-gray-100" data-id="element-22">
              <div className="text-base font-semibold text-brand-purple mb-1" data-id="element-23">
                Phase 1
              </div>
              <div className="text-xs text-gray-600" data-id="element-24">
                15 years leading software sales teams, mastering the human
                element of B2B sales
              </div>
            </div>
            <div className="bg-gradient-to-br from-brand-purple/5 to-brand-neon/5 p-3 rounded-xl border border-gray-100" data-id="element-25">
              <div className="text-base font-semibold text-brand-purple mb-1" data-id="element-26">
                Phase 2
              </div>
              <div className="text-xs text-gray-600" data-id="element-27">
                5 years with Ready Set Sell, proving process automation drives
                real results
              </div>
            </div>
            <div className="bg-gradient-to-br from-brand-purple/5 to-brand-neon/5 p-3 rounded-xl border border-gray-100" data-id="element-28">
              <div className="text-base font-semibold text-brand-purple mb-1" data-id="element-29">
                Phase 3
              </div>
              <div className="text-xs text-gray-600" data-id="element-30">
                Today: Merging human expertise with AI to deliver
                personalization at scale
              </div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-xl border border-gray-100" data-id="element-31">
            <div className="flex items-start space-x-3" data-id="element-32">
              <div className="p-1.5 bg-brand-purple/10 rounded-lg" data-id="element-33">
                <RocketIcon className="w-4 h-4 text-brand-purple" data-id="element-34" />
              </div>
              <div data-id="element-35">
                <h3 className="font-semibold text-brand-purple text-base mb-1" data-id="element-36">
                  Why Now?
                </h3>
                <p className="text-xs text-gray-600" data-id="element-37">
                  We're at a unique intersection where human sales expertise
                  meets cutting-edge AI capabilities. This isn't just about
                  automation—it's about amplifying the human touch through
                  technology, making genuine personalization possible at scale.
                </p>
              </div>
            </div>
          </div>
        </div>
  },
  // The Secret Sauce slide
  {
    icon: <BrainIcon className="w-8 h-8 text-brand-purple" data-id="element-38" />,
    title: 'The Secret Sauce',
    content: <div className="space-y-6" data-id="element-39">
          <div className="grid grid-cols-2 gap-4" data-id="element-40">
            <div className="bg-white p-4 rounded-xl border border-gray-100" data-id="element-41">
              <div className="flex items-start space-x-3" data-id="element-42">
                <div className="p-1.5 bg-brand-purple/10 rounded-lg" data-id="element-43">
                  <LayersIcon className="w-4 h-4 text-brand-purple" data-id="element-44" />
                </div>
                <div data-id="element-45">
                  <h3 className="font-medium text-brand-purple text-sm" data-id="element-46">
                    Lead Scoring Agent
                  </h3>
                  <p className="text-xs text-gray-600 mt-1" data-id="element-47">
                    Evaluates and prioritizes leads based on industry alignment,
                    project experience, and strategic fit, ensuring your team
                    engages only with high-potential prospects.
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white p-4 rounded-xl border border-gray-100" data-id="element-48">
              <div className="flex items-start space-x-3" data-id="element-49">
                <div className="p-1.5 bg-brand-purple/10 rounded-lg" data-id="element-50">
                  <BrainIcon className="w-4 h-4 text-brand-purple" data-id="element-51" />
                </div>
                <div data-id="element-52">
                  <h3 className="font-medium text-brand-purple text-sm" data-id="element-53">
                    Personality Assessment Agent
                  </h3>
                  <p className="text-xs text-gray-600 mt-1" data-id="element-54">
                    Utilizes LinkedIn profiles to analyze prospect communication
                    styles, decision-making patterns, and potential
                    objections—enabling more precise and effective outreach.
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white p-4 rounded-xl border border-gray-100" data-id="element-55">
              <div className="flex items-start space-x-3" data-id="element-56">
                <div className="p-1.5 bg-brand-purple/10 rounded-lg" data-id="element-57">
                  <RocketIcon className="w-4 h-4 text-brand-purple" data-id="element-58" />
                </div>
                <div data-id="element-59">
                  <h3 className="font-medium text-brand-purple text-sm" data-id="element-60">
                    Outreach Generator Agent
                  </h3>
                  <p className="text-xs text-gray-600 mt-1" data-id="element-61">
                    Crafts highly personalized LinkedIn messaging sequences that
                    resonate with each individual prospect, significantly
                    increasing response rates.
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white p-4 rounded-xl border border-gray-100" data-id="element-62">
              <div className="flex items-start space-x-3" data-id="element-63">
                <div className="p-1.5 bg-brand-purple/10 rounded-lg" data-id="element-64">
                  <SendIcon className="w-4 h-4 text-brand-purple" data-id="element-65" />
                </div>
                <div data-id="element-66">
                  <h3 className="font-medium text-brand-purple text-sm" data-id="element-67">
                    Auto-Pilot Mode
                  </h3>
                  <p className="text-xs text-gray-600 mt-1" data-id="element-68">
                    Intelligent scheduling system sends follow-ups at optimal
                    times, maintaining consistent engagement while respecting
                    LinkedIn's limits.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4" data-id="element-69">
            <div className="bg-gradient-to-br from-brand-purple/5 to-brand-neon/5 p-4 rounded-xl text-center border border-gray-100" data-id="element-70">
              <div className="text-3xl font-bold text-brand-purple mb-1" data-id="element-71">
                3x
              </div>
              <div className="text-sm text-gray-600" data-id="element-72">More Responses</div>
            </div>
            <div className="bg-gradient-to-br from-brand-purple/5 to-brand-neon/5 p-4 rounded-xl text-center border border-gray-100" data-id="element-73">
              <div className="text-3xl font-bold text-brand-purple mb-1" data-id="element-74">
                1k+
              </div>
              <div className="text-sm text-gray-600" data-id="element-75">Prospects/Month</div>
            </div>
            <div className="bg-gradient-to-br from-brand-purple/5 to-brand-neon/5 p-4 rounded-xl text-center border border-gray-100" data-id="element-76">
              <div className="text-3xl font-bold text-brand-purple mb-1" data-id="element-77">
                70%
              </div>
              <div className="text-sm text-gray-600" data-id="element-78">Less Prospecting Time</div>
            </div>
          </div>
        </div>
  },
  // Customer Success: Tech Company slide
  {
    icon: <TrendingUpIcon className="w-8 h-8 text-brand-purple" data-id="element-79" />,
    title: 'Customer Success: Tech Company',
    content: <div className="space-y-3" data-id="element-80">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/hsUwqNfxREXRodpRcA5sWL/Screenshot_2025-05-07_at_17.37.08.png" alt="Tech Company Campaign Results" className="w-full h-[200px] object-contain rounded-lg" data-id="element-81" />
          <div className="grid grid-cols-2 gap-3" data-id="element-82">
            <div className="bg-white p-3 rounded-lg border border-gray-100" data-id="element-83">
              <div className="text-sm font-medium text-brand-purple" data-id="element-84">
                Key Metrics
              </div>
              <ul className="text-xs text-gray-600 mt-1 space-y-0.5" data-id="element-85">
                <li data-id="element-86">• 34.6% Connection Rate</li>
                <li data-id="element-87">• 87.3% Follow-up Rate</li>
                <li data-id="element-88">• 38.2% Response Rate</li>
              </ul>
            </div>
            <div className="bg-white p-3 rounded-lg border border-gray-100" data-id="element-89">
              <div className="text-sm font-medium text-brand-purple" data-id="element-90">
                Campaign Size
              </div>
              <div className="text-xl font-bold text-brand-purple mt-1" data-id="element-91">
                1,090
              </div>
              <div className="text-xs text-gray-600" data-id="element-92">Prospects Reached</div>
            </div>
          </div>
        </div>
  },
  // Customer Success: Manufacturing slide
  {
    icon: <TrendingUpIcon className="w-8 h-8 text-brand-purple" data-id="element-93" />,
    title: 'Customer Success: Manufacturing',
    content: <div className="space-y-3" data-id="element-94">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/vrrYdRWYGsnJJUdx7559Ax/Screenshot_2025-05-07_at_17.37.49.png" alt="Manufacturing Campaign Results" className="w-full h-[200px] object-contain rounded-lg" data-id="element-95" />
          <div className="grid grid-cols-2 gap-3" data-id="element-96">
            <div className="bg-white p-3 rounded-lg border border-gray-100" data-id="element-97">
              <div className="text-sm font-medium text-brand-purple" data-id="element-98">
                Key Metrics
              </div>
              <ul className="text-xs text-gray-600 mt-1 space-y-0.5" data-id="element-99">
                <li data-id="element-100">• 48.2% Connection Rate</li>
                <li data-id="element-101">• 89.5% Follow-up Rate</li>
                <li data-id="element-102">• 20.2% Response Rate</li>
              </ul>
            </div>
            <div className="bg-white p-3 rounded-lg border border-gray-100" data-id="element-103">
              <div className="text-sm font-medium text-brand-purple" data-id="element-104">
                Campaign Size
              </div>
              <div className="text-xl font-bold text-brand-purple mt-1" data-id="element-105">
                965
              </div>
              <div className="text-xs text-gray-600" data-id="element-106">Prospects Reached</div>
            </div>
          </div>
        </div>
  },
  // Customer Success: Learning slide
  {
    icon: <TrendingUpIcon className="w-8 h-8 text-brand-purple" data-id="element-107" />,
    title: 'Customer Success: Learning',
    content: <div className="space-y-3" data-id="element-108">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/4gSNsAiigmRmJotRMna15U/Screenshot_2025-05-07_at_17.38.39.png" alt="Retail Campaign Results" className="w-full h-[200px] object-contain rounded-lg" data-id="element-109" />
          <div className="grid grid-cols-2 gap-3" data-id="element-110">
            <div className="bg-white p-3 rounded-lg border border-gray-100" data-id="element-111">
              <div className="text-sm font-medium text-brand-purple" data-id="element-112">
                Key Metrics
              </div>
              <ul className="text-xs text-gray-600 mt-1 space-y-0.5" data-id="element-113">
                <li data-id="element-114">• 61.3% Connection Rate</li>
                <li data-id="element-115">• 71.4% Follow-up Rate</li>
                <li data-id="element-116">• 22.8% Response Rate</li>
              </ul>
            </div>
            <div className="bg-white p-3 rounded-lg border border-gray-100" data-id="element-117">
              <div className="text-sm font-medium text-brand-purple" data-id="element-118">
                Campaign Size
              </div>
              <div className="text-xl font-bold text-brand-purple mt-1" data-id="element-119">
                1,211
              </div>
              <div className="text-xs text-gray-600" data-id="element-120">Prospects Reached</div>
            </div>
          </div>
        </div>
  },
  // Implementation slide
  {
    icon: <LayersIcon className="w-8 h-8 text-brand-purple" data-id="element-121" />,
    title: 'Implementation',
    content: <div className="relative" data-id="element-122">
          <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-brand-purple/20" data-id="element-123"></div>
          <div className="space-y-4" data-id="element-124">
            <div className="flex items-start" data-id="element-125">
              <div className="bg-brand-neon rounded-full p-1.5 relative z-10 mr-3" data-id="element-126">
                <span className="text-xs font-bold" data-id="element-127">1</span>
              </div>
              <div className="bg-white p-3 rounded-xl border border-gray-100 flex-1" data-id="element-128">
                <h3 className="font-medium text-brand-purple text-sm mb-1" data-id="element-129">
                  Week 1: Setup
                </h3>
                <div className="flex space-x-4" data-id="element-130">
                  <div className="flex items-center space-x-1.5" data-id="element-131">
                    <LayersIcon className="w-3.5 h-3.5 text-brand-purple" data-id="element-132" />
                    <span className="text-xs text-gray-600" data-id="element-133">
                      Strategy Definition
                    </span>
                  </div>
                  <div className="flex items-center space-x-1.5" data-id="element-134">
                    <BrainIcon className="w-3.5 h-3.5 text-brand-purple" data-id="element-135" />
                    <span className="text-xs text-gray-600" data-id="element-136">
                      Data Collection
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex items-start" data-id="element-137">
              <div className="bg-brand-neon rounded-full p-1.5 relative z-10 mr-3" data-id="element-138">
                <span className="text-xs font-bold" data-id="element-139">2</span>
              </div>
              <div className="bg-white p-3 rounded-xl border border-gray-100 flex-1" data-id="element-140">
                <h3 className="font-medium text-brand-purple text-sm mb-1" data-id="element-141">
                  Week 2: AI Configuration
                </h3>
                <div className="flex space-x-4" data-id="element-142">
                  <div className="flex items-center space-x-1.5" data-id="element-143">
                    <BrainIcon className="w-3.5 h-3.5 text-brand-purple" data-id="element-144" />
                    <span className="text-xs text-gray-600" data-id="element-145">
                      AI Configuration
                    </span>
                  </div>
                  <div className="flex items-center space-x-1.5" data-id="element-146">
                    <RocketIcon className="w-3.5 h-3.5 text-brand-purple" data-id="element-147" />
                    <span className="text-xs text-gray-600" data-id="element-148">
                      Initial Testing
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex items-start" data-id="element-149">
              <div className="bg-brand-neon rounded-full p-1.5 relative z-10 mr-3" data-id="element-150">
                <span className="text-xs font-bold" data-id="element-151">3</span>
              </div>
              <div className="bg-white p-3 rounded-xl border border-gray-100 flex-1" data-id="element-152">
                <h3 className="font-medium text-brand-purple text-sm mb-1" data-id="element-153">
                  Week 3: Campaign Launch
                </h3>
                <div className="flex space-x-4" data-id="element-154">
                  <div className="flex items-center space-x-1.5" data-id="element-155">
                    <RocketIcon className="w-3.5 h-3.5 text-brand-purple" data-id="element-156" />
                    <span className="text-xs text-gray-600" data-id="element-157">
                      Campaign Launch
                    </span>
                  </div>
                  <div className="flex items-center space-x-1.5" data-id="element-158">
                    <LayersIcon className="w-3.5 h-3.5 text-brand-purple" data-id="element-159" />
                    <span className="text-xs text-gray-600" data-id="element-160">
                      Performance Monitoring
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex items-start" data-id="element-161">
              <div className="bg-brand-neon rounded-full p-1.5 relative z-10 mr-3" data-id="element-162">
                <span className="text-xs font-bold" data-id="element-163">Ongoing</span>
              </div>
              <div className="bg-white p-3 rounded-xl border border-gray-100 flex-1" data-id="element-164">
                <h3 className="font-medium text-brand-purple text-sm mb-1" data-id="element-165">
                  Ongoing: Optimization
                </h3>
                <div className="flex space-x-4" data-id="element-166">
                  <div className="flex items-center space-x-1.5" data-id="element-167">
                    <LayersIcon className="w-3.5 h-3.5 text-brand-purple" data-id="element-168" />
                    <span className="text-xs text-gray-600" data-id="element-169">
                      Weekly Optimization
                    </span>
                  </div>
                  <div className="flex items-center space-x-1.5" data-id="element-170">
                    <RocketIcon className="w-3.5 h-3.5 text-brand-purple" data-id="element-171" />
                    <span className="text-xs text-gray-600" data-id="element-172">
                      Results Reporting
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
  },
  // Trusted by Industry Leaders slide
  {
    icon: <BriefcaseIcon className="w-8 h-8 text-brand-purple" data-id="element-173" />,
    title: 'Trusted by Industry Leaders',
    content: <div className="flex items-center justify-center h-[calc(100%-3rem)]" data-id="element-174">
          <div className="flex items-center justify-center w-full" data-id="element-175">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/wLUbNacMMg8JenvsCXMfGp/Screenshot_2025-05-07_at_19.02.15.png" alt="Partner Companies" className="w-full h-full object-contain max-h-[300px]" data-id="element-176" />
          </div>
        </div>
  },
  // Let's Connect slide
  {
    icon: <CalendarIcon className="w-8 h-8 text-brand-purple" data-id="element-177" />,
    title: "Let's Connect",
    content: <div className="flex items-center justify-center h-[calc(100%-3rem)]" data-id="element-178">
          <div className="flex flex-col items-center space-y-8 w-full max-w-lg mx-auto" data-id="element-179">
            <div className="flex flex-col items-center space-y-4" data-id="element-180">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/oFeDsrpC9yrQ6ZQarQEx69/1548848296654_%281%29.jpg" alt="Irina Stoicescu" className="w-32 h-32 rounded-full object-cover border-4 border-brand-purple shadow-lg" data-id="element-181" />
              <div className="text-center" data-id="element-182">
                <h3 className="text-2xl font-bold text-brand-purple mb-2" data-id="element-183">
                  Irina Stoicescu
                </h3>
                <a href="https://www.linkedin.com/in/irina-lam/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-4 py-2 bg-[#0A66C2] text-white rounded-lg hover:bg-[#004182] transition-colors" data-id="element-184">
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor" data-id="element-185">
                    <path d="M20.5 2h-17A1.5 1.5 0 002 3.5v17A1.5 1.5 0 003.5 22h17a1.5 1.5 0 001.5-1.5v-17A1.5 1.5 0 0020.5 2zM8 19H5v-9h3zM6.5 8.25A1.75 1.75 0 118.3 6.5a1.78 1.78 0 01-1.8 1.75zM19 19h-3v-4.74c0-1.42-.6-1.93-1.38-1.93A1.74 1.74 0 0013 14.19a.66.66 0 000 .14V19h-3v-9h2.9v1.3a3.11 3.11 0 012.7-1.4c1.55 0 3.36.86 3.36 3.66z" data-id="element-186"></path>
                  </svg>
                  Connect on LinkedIn
                </a>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-6 w-full" data-id="element-187">
              <a href="https://usegigi.ai" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center px-6 py-4 bg-gradient-to-br from-brand-purple/5 to-brand-neon/5 rounded-xl border border-gray-100 text-brand-purple font-medium hover:bg-gray-50 transition-colors" data-id="element-188">
                usegigi.ai
              </a>
              <a href="https://set-sell.com" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center px-6 py-4 bg-gradient-to-br from-brand-purple/5 to-brand-neon/5 rounded-xl border border-gray-100 text-brand-purple font-medium hover:bg-gray-50 transition-colors" data-id="element-189">
                set-sell.com
              </a>
            </div>
          </div>
        </div>
  }];
  // Then filter out Dominik's slides
  const slides = allSlides.filter(slide => {
    const content = slide.content.toString();
    return !content.includes('Dominik Manzke') && !content.includes('Hey Dominik,');
  });
  const nextSlide = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    }
  };
  const prevSlide = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };
  return <div className="max-w-4xl mx-auto px-4 py-8 md:py-16" data-id="element-190">
      <header className="mb-12 text-center" data-id="element-191">
        <h1 className="text-3xl md:text-4xl font-bold text-brand-purple mb-4" data-id="element-192">
          AI-Powered B2B Sales Automation Proposal
        </h1>
      </header>
      <div className="relative" data-id="element-193">
        <Slide key={currentSlide} icon={slides[currentSlide].icon} title={slides[currentSlide].title} content={slides[currentSlide].content} data-id="element-194" />
        <div className="absolute left-0 top-1/2 -translate-y-1/2 -ml-4 md:-ml-8" data-id="element-195">
          <button onClick={prevSlide} disabled={currentSlide === 0} className={`p-2 rounded-full transition-colors ${currentSlide === 0 ? 'text-gray-300 cursor-not-allowed' : 'text-brand-purple hover:bg-brand-purple/10'}`} data-id="element-196">
            <ChevronLeftIcon size={24} data-id="element-197" />
          </button>
        </div>
        <div className="absolute right-0 top-1/2 -translate-y-1/2 -mr-4 md:-mr-8" data-id="element-198">
          <button onClick={nextSlide} disabled={currentSlide === slides.length - 1} className={`p-2 rounded-full transition-colors ${currentSlide === slides.length - 1 ? 'text-gray-300 cursor-not-allowed' : 'text-brand-purple hover:bg-brand-purple/10'}`} data-id="element-199">
            <ChevronRightIcon size={24} data-id="element-200" />
          </button>
        </div>
      </div>
      <Navigation totalSlides={slides.length} currentSlide={currentSlide} setCurrentSlide={setCurrentSlide} data-id="element-201" />
    </div>;
}